<script lang="ts">
  import BlankDie from '../../dice-tray/die-blank.svelte';
  import Text from '../../components/text.svelte';
  import Icon from '../../icon/icon.svelte';
  import Hover from '../../components/hover.svelte';
  import type { Die as DieObject } from '../../models/die.type';
  import type { DieFace } from '../../models/face.type';
  import type { Technique } from '../../models/techniques.type';

  interface Props {
    data: Technique;
    searchDie?: Array<DieObject>;
  }

  let { data, searchDie = [] }: Props = $props();

  /**
   * Hidden unless every ingredient is somewhere on the faces of the dice being
   * filtered by. An empty filter shows everything.
   */
  let display = $derived.by(() => {
    const faces = new Set<DieFace>(searchDie.flatMap((die) => die.faces.flat()));

    return !faces.size || data.ingredients.every((ingredient: DieFace) => faces.has(ingredient));
  });

  /**
   * A requirement is a gate the technique checks before it can be used. Some
   * carry only text -- the markup used to be gated on `icon`, so those rendered
   * as nothing at all and the technique looked unconditional.
   */
  let requirements = $derived(
    (data.requirements ?? []).filter((requirement) => requirement.icon || requirement.text),
  );
</script>

{#if display}
  <div class="container entry">
    <div class="recipe">
      {#each data.ingredients as ingredient, index (index)}
        <div class="ingredient">
          <div class="die-container">
            <BlankDie dieType={ingredient} />
          </div>
          <span class="ingredient-label">{ingredient}</span>
        </div>
      {/each}
    </div>

    <div class="information">
      <div class="heading">
        <h2>{data.title}</h2>

        {#if data.tags?.length}
          <div class="tags">
            {#each data.tags as tag}
              <span class="tag {tag}">{tag}</span>
            {/each}
          </div>
        {/if}
      </div>

      <Text data={data.text} class="text" />

      {#if requirements.length}
        <div class="requirements">
          {#each requirements as requirement, index (index)}
            <div class="requirement">
              {#if requirement.icon}
                <div class="requirement-icon">
                  <Icon name={requirement.icon} width="20px" height="20px" />
                  {#if requirement.iconBonus}
                    <Icon name={requirement.iconBonus} width="20px" height="20px" />
                  {/if}

                  <Hover>
                    <div class="hover-icon">
                      <Icon name={requirement.icon} width="40px" height="40px" />
                      {#if requirement.iconBonus}
                        <Icon name={requirement.iconBonus} width="40px" height="40px" />
                      {/if}
                    </div>
                    <br />
                    <p>{requirement.text}</p>
                  </Hover>
                </div>
              {/if}

              <span class="requirement-text">{requirement.text}</span>
            </div>
          {/each}
        </div>
      {/if}
    </div>
  </div>
{/if}

<style lang="less">
  @import '../../../defines.less';

  .container {
    display: grid;
    // Recipes run to three ingredients at most, and the column is held at that
    // width whether or not a recipe fills it -- an `auto` column left every
    // name starting at a different x, which a card edge used to hide and a
    // page of running text does not. A blank die is --dice-size + --dice-gap
    // wide (dice-tray/die-blank.svelte), laid out with --dice-gap between.
    grid-template-columns:
      calc(3 * (var(--dice-size) + var(--dice-gap)) + 2 * var(--dice-gap))
      minmax(0, 1fr);
    grid-gap: 14px;
    align-items: start;

    text-align: left;

    // No card -- the recipe's own dice are the thing the eye lands on, and the
    // rule above the row is what separates it from the recipe before it.

    @media @mobile {
      grid-template-columns: minmax(0, 1fr);
      justify-items: center;
      text-align: center;
    }
  }

  .recipe {
    display: flex;
    align-items: flex-start;
    grid-gap: var(--dice-gap);

    .ingredient {
      display: flex;
      flex-direction: column;
      align-items: center;
      grid-gap: 2px;
    }

    .die-container {
      position: relative;
    }

    // The faces alone are hard to tell apart at a glance until you know them
    // all, and a recipe is the one place the exact face matters.
    .ingredient-label {
      font-size: 0.6rem;
      letter-spacing: 0.03em;
      text-transform: uppercase;
      opacity: 0.5;
    }
  }

  .information {
    display: flex;
    flex-direction: column;
    grid-gap: 6px;
    min-width: 0;
    width: 100%;
  }

  .heading {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    // Left-packed, not space-between: without a card edge to push against, a
    // right-aligned chip just floats off on its own halfway across the page.
    justify-content: flex-start;
    grid-gap: 8px;

    h2 {
      font-size: 1.1rem;
      text-transform: capitalize;
    }
  }

  .tags {
    display: flex;
    flex-wrap: wrap;
    grid-gap: 4px;

    .tag {
      padding: 2px 9px;
      font-size: 0.6rem;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      opacity: 0.7;
      background: var(--background-colour-dark);
      border: 1px solid var(--background-colour-light);
      border-radius: 999px;

      // Only the handful that say something about what the technique does to
      // you or to them get a colour; the rest stay neutral.
      &.damage {
        color: var(--red);
      }
      &.defence {
        color: var(--light-blue);
      }
      &.heal {
        color: var(--green);
      }
      &.buff {
        color: var(--cyan);
      }
      &.debuff {
        color: var(--purple);
      }
      &.suffer {
        color: var(--orange);
      }
    }
  }

  .container :global(.text) {
    font-size: 0.9rem;
    opacity: 0.85;
  }

  .requirements {
    display: flex;
    flex-direction: column;
    grid-gap: 4px;

    .requirement {
      display: flex;
      align-items: center;
      grid-gap: 6px;
      font-size: 0.8rem;
    }

    .requirement-icon {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 26px;
      height: 26px;
      border-radius: 999px;
      background: #888;
      box-shadow:
        inset 2px 2px 3px 0 #aaa,
        inset -2px -2px 3px #666;
      flex: 0 0 auto;

      .hover-icon {
        width: 40px;
        height: 40px;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: auto;
      }
    }

    .requirement-text {
      opacity: 0.7;

      &::before {
        content: '\26A0\00a0';
        opacity: 0.6;
      }
    }
  }
</style>
